import type { MissingInput, PatternsStatus, PatternView, RuleWithoutPattern } from "../../shared/patterns.js";
import { Page } from "../Page.js";
import { DependencyView } from "../components/DependencyView.js";
import { patternsSummary, stackChip } from "../components/PatternBits.js";
import { stackIcon } from "../components/StackIcon.js";
import { favoriteStar, favoritesFirst } from "../components/Favorite.js";
import { ViewToggle } from "../components/ViewToggle.js";
import { stackKey } from "../../shared/stack.js";
import { debounce, h, patch } from "../dom.js";
import { ago, amount, stackDetail, stackName } from "../format.js";

const PAGE_SIZE = 100;

type View = "patterns" | "missing" | "dependencies";

/** "patterns/<tab>/<key>" of the current hash. */
function hashParts(): string[] {
  return location.hash.replace(/^#\/?/, "").split("/");
}

/**
 * Every pattern on the network (what it makes, what it needs, where it sits)
 * and, in a second tab, the ingredients no pattern makes. Each of those is
 * either produced by an always-running GTNH setup (mark it auto-prod) or
 * needs a pattern or a manual supply.
 */
export class PatternsPage extends Page {
  readonly title = "Patterns";
  private readonly hash = hashParts();
  private view: View = this.hash[1] === "missing" || this.hash[1] === "dependencies" ? this.hash[1] : "patterns";
  private readonly dependencies = new DependencyView(
    this.ctx,
    this.hash[1] === "dependencies" && this.hash[2] ? decodeURIComponent(this.hash[2]) : null,
    (key) => history.replaceState(null, "", key ? `#/patterns/dependencies/${encodeURIComponent(key)}` : "#/patterns/dependencies"),
  );
  private status: PatternsStatus | null = null;
  private items: PatternView[] = [];
  private total = 0;
  private noPattern: RuleWithoutPattern[] = [];
  private missingItems: MissingInput[] = [];

  private readonly summary = h("div");
  private readonly refresh = h("button", { class: "primary", onclick: () => void this.load() }, "Load patterns");
  private readonly tabs = h("div", { class: "segmented", role: "tablist" });
  private readonly body = h("div");

  // Patterns tab
  private readonly rulesNote = h("div");
  private readonly search = h("input", { type: "search", placeholder: "Search outputs, inputs or interfaces…" });
  private readonly filter = h(
    "select",
    null,
    h("option", { value: "" }, "All patterns"),
    h("option", { value: "missing" }, "With an input nothing makes"),
  );
  private readonly count = h("span", { class: "muted" });
  private readonly patternsMode = new ViewToggle("patterns", () => this.render());
  private readonly missingMode = new ViewToggle("missing", () => this.renderMissing());
  private readonly list = h("div");
  private readonly more = h("button", { onclick: () => void this.fetch(true) }, "Show more");
  private readonly patternsView = h(
    "div",
    null,
    h("p", { class: "muted small" }, "Inputs are green when the network can craft them, blue when marked auto-prod, red when nothing makes them (see Missing inputs)."),
    this.rulesNote,
    h("div", { class: "toolbar" }, this.search, this.filter, h("span", { class: "spacer" }), this.count, this.patternsMode.el),
    this.list,
    h("div", { class: "table-foot" }, h("span"), this.more),
  );

  // Missing inputs tab
  private readonly stockButton = h("button", { onclick: () => void this.readStock() }, "Refresh stock");
  private readonly missingSearch = h("input", { type: "search", placeholder: "Filter ingredients…" });
  private readonly missing = h("div");
  private readonly autoProd = h("div");
  private readonly missingTitle = h("h2");
  private readonly autoProdTitle = h("h2");
  private readonly missingView = h(
    "div",
    null,
    h(
      "p",
      { class: "muted small" },
      "Ingredients that no pattern on the network makes, so any craft that needs them depends on what is in stock. " +
        "Mark the ones an always-running setup produces as auto-prod: they stop counting as missing, and a failed craft short of one says it is waiting on auto-prod. " +
        "The others need a pattern, or a regular manual supply.",
    ),
    h("div", { class: "toolbar" }, this.missingSearch, h("span", { class: "spacer" }), this.missingMode.el, this.stockButton),
    this.missingTitle,
    this.missing,
    this.autoProdTitle,
    this.autoProd,
  );

  mount(): void {
    const reload = debounce(() => void this.fetch(false), 200);
    this.search.addEventListener("input", reload);
    this.filter.addEventListener("change", reload);
    this.missingSearch.addEventListener("input", debounce(() => this.renderMissing(), 150));
    this.el.append(
      h(
        "div",
        { class: "card" },
        h("div", { class: "row" }, h("h2", null, "Patterns"), h("span", { class: "spacer" }), this.refresh),
        this.summary,
        this.tabs,
      ),
      this.body,
    );
    this.listen("patterns", () => {
      const before = this.status;
      this.status = this.ctx.live.patterns;
      this.renderSummary();
      // A finished load or a changed auto-prod mark changes both lists.
      if (!this.status?.loading && (before?.at !== this.status?.at || before?.autoProd !== this.status?.autoProd || before?.rulesWithoutPattern !== this.status?.rulesWithoutPattern)) {
        void this.fetch(false);
      }
    });
    this.listen("link", () => this.renderSummary());
    // The server puts patterns that make a favorite first; the missing inputs are sorted here.
    this.listen("favorites", debounce(() => void this.fetch(false), 300));
    this.show(this.view);
    void this.start();
  }

  private async start(): Promise<void> {
    this.status = (await this.attempt(() => this.ctx.api.patternsStatus())) ?? null;
    this.renderSummary();
    await this.fetch(false);
  }

  private show(view: View): void {
    this.view = view;
    if (view !== "dependencies") history.replaceState(null, "", view === "missing" ? "#/patterns/missing" : "#/patterns");
    else if (hashParts()[1] !== "dependencies") history.replaceState(null, "", "#/patterns/dependencies");
    this.body.replaceChildren(view === "missing" ? this.missingView : view === "dependencies" ? this.dependencies.el : this.patternsView);
    this.renderTabs();
  }

  private renderTabs(): void {
    const tab = (view: View, label: string) =>
      h("button", { class: this.view === view ? "active" : "", role: "tab", "aria-selected": String(this.view === view), onclick: () => this.show(view) }, label);
    const missing = this.status?.missing;
    patch(this.tabs, 
      tab("patterns", this.status?.count ? `Patterns (${this.status.count})` : "Patterns"),
      tab("missing", missing ? `Missing inputs (${missing})` : "Missing inputs"),
      tab("dependencies", "Dependencies"),
    );
  }

  private renderSummary(): void {
    patch(this.summary, patternsSummary(this.status, Boolean(this.ctx.live.link?.connected)));
    this.refresh.disabled = Boolean(this.status?.loading);
    this.refresh.textContent = this.status?.at ? "Reload patterns" : "Load patterns";
    this.renderTabs();
  }

  private async load(): Promise<void> {
    const res = await this.attempt(() => this.ctx.api.refreshPatterns());
    if (res) this.ctx.toast(`Loaded ${res.count} patterns`);
  }

  /** Reloads the pattern list (from the first page unless `append`) and the missing inputs. */
  private async fetch(append: boolean): Promise<void> {
    const offset = append ? this.items.length : 0;
    const page = await this.attempt(() =>
      this.ctx.api.patterns({ q: this.search.value, filter: this.filter.value, offset, limit: PAGE_SIZE }),
    );
    if (!page) return;
    this.items = append ? [...this.items, ...page.items] : page.items;
    this.total = page.total;
    if (!append) await Promise.all([this.fetchMissing(), this.dependencies.load()]);
    this.render();
  }

  private async fetchMissing(): Promise<void> {
    const data = await this.attempt(() => this.ctx.api.missingInputs());
    if (!data) return;
    this.status = data.status;
    this.missingItems = data.missing;
    this.noPattern = data.rulesWithoutPattern;
    this.renderSummary();
    this.renderMissing();
  }

  private async readStock(): Promise<void> {
    this.stockButton.disabled = true;
    const res = await this.attempt(() => this.ctx.api.refreshMissingStock());
    this.stockButton.disabled = false;
    if (res) {
      this.ctx.toast(`Read stock of ${res.read} ingredients`);
      await this.fetchMissing();
    }
  }

  private async toggle(item: MissingInput): Promise<void> {
    const on = !item.autoProd;
    const res = await this.attempt(() => this.ctx.api.setAutoProd(item.stack, on), on ? `${stackName(item.stack)} marked auto-prod` : `${stackName(item.stack)} is no longer auto-prod`);
    if (res) await this.fetch(false);
  }

  // ---------------------------------------------------------- patterns tab

  private render(): void {
    this.count.textContent = this.status?.count ? `${this.total} of ${this.status.count}` : "";
    this.more.hidden = this.items.length >= this.total;
    patch(this.rulesNote, 
      ...(this.noPattern.length
        ? [h(
            "div",
            { class: "card notice" },
            h("strong", null, `${this.noPattern.length} rule${this.noPattern.length === 1 ? " has" : "s have"} no pattern`),
            ", so the Butler can never craft ",
            this.noPattern.length === 1 ? "it" : "them",
            ": ",
            ...this.noPattern.slice(0, 30).flatMap((r, i) => [i ? ", " : "", stackName(r.stack)]),
            this.noPattern.length > 30 ? ` and ${this.noPattern.length - 30} more` : "",
            ".",
          )]
        : []),
    );
    if (!this.items.length) {
      patch(this.list, 
        h("p", { class: "muted empty" }, this.status?.count ? "No pattern matches." : "No patterns loaded yet."),
      );
      return;
    }
    if (this.patternsMode.cards) {
      patch(this.list, h("div", { class: "mini-grid pattern-grid" }, ...this.items.map((p) => this.card(p))));
      return;
    }
    patch(this.list, 
      h(
        "div",
        { class: "table-wrap" },
        h(
          "table",
          { class: "patterns" },
          h("thead", null, h("tr", null, h("th", null, "Makes"), h("th", null, "Needs"), h("th", null, "Interface"))),
          h("tbody", null, ...this.items.map((p) => this.row(p))),
        ),
      ),
    );
  }

  /** A pattern as a card: its main output with its picture, the rest as chips, then where it sits. */
  private card(p: PatternView): HTMLElement {
    const [main, ...others] = p.outputs;
    const favorite = p.outputs.some((o) => this.ctx.live.favorites.has(stackKey(o.stack)));
    return h(
      "article",
      { key: p.id, class: `mini-card pattern-card${p.missing ? " status-low" : ""}${favorite ? " favorite" : ""}` },
      main
        ? h(
            "div",
            { class: "mc-head" },
            h("span", { class: "slot" }, stackIcon(main.stack, "lg")),
            h("span", { class: "mc-name" }, `${amount(main.amount, main.stack.type)} × ${stackName(main.stack)}`),
            favoriteStar(this.ctx, stackKey(main.stack), main.stack),
          )
        : null,
      others.length ? h("div", { class: "chips" }, ...others.map((o) => stackChip(o.stack, o.amount))) : null,
      h("div", { class: "chips" }, ...p.inputs.map((i) => stackChip(i.stack, i.amount, i.source))),
      p.missing ? h("div", { class: "small error" }, `${p.missing} input${p.missing === 1 ? "" : "s"} nothing makes`) : null,
      h("div", { class: "muted small mc-detail" }, `${p.interface || "Unnamed interface"} · slot ${p.slot + 1} · ${p.crafting ? "crafting" : "processing"}`),
    );
  }

  private row(p: PatternView): HTMLElement {
    return h(
      "tr",
      { key: p.id, class: p.missing ? "pattern-blocked" : "" },
      h(
        "td",
        null,
        h("div", { class: "chips" }, p.outputs[0] ? favoriteStar(this.ctx, stackKey(p.outputs[0].stack), p.outputs[0].stack) : null, ...p.outputs.map((o) => stackChip(o.stack, o.amount))),
      ),
      h(
        "td",
        null,
        h("div", { class: "chips" }, ...p.inputs.map((i) => stackChip(i.stack, i.amount, i.source))),
        p.missing ? h("div", { class: "small error" }, `${p.missing} input${p.missing === 1 ? "" : "s"} nothing makes`) : null,
      ),
      h(
        "td",
        { class: "small" },
        h("div", null, p.interface || "Unnamed interface"),
        h("div", { class: "muted" }, `Slot ${p.slot + 1} · ${p.crafting ? "Crafting" : "Processing"}`),
      ),
    );
  }

  // ---------------------------------------------------- missing inputs tab

  private renderMissing(): void {
    const words = this.missingSearch.value.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const shown = this.missingItems.filter((m) => {
      const text = `${m.stack.label ?? ""}\n${m.key}\n${m.needs.map((s) => s.label ?? s.name).join("\n")}`.toLowerCase();
      return words.every((w) => text.includes(w));
    });
    const favorites = this.ctx.live.favorites;
    const pinned = favoritesFirst(shown, (m) => favorites.has(m.key));
    const missing = pinned.filter((m) => !m.autoProd);
    const autoProd = pinned.filter((m) => m.autoProd);
    this.missingTitle.textContent = `Nothing makes these (${missing.length})`;
    this.autoProdTitle.textContent = `Auto-prod (${autoProd.length})`;
    patch(this.missing, 
      this.missingTable(missing, this.status?.count ? "Every ingredient is autocraftable or marked auto-prod." : "Load the patterns first."),
    );
    patch(this.autoProd, this.missingTable(autoProd, "Nothing is marked auto-prod yet."));
  }

  private missingTable(items: MissingInput[], empty: string): HTMLElement {
    if (!items.length) return h("p", { class: "muted empty" }, empty);
    const toggle = (m: MissingInput) =>
      h("button", { class: `small${m.autoProd ? "" : " primary"}`, onclick: () => void this.toggle(m) }, m.autoProd ? "Unmark" : "Mark auto-prod");
    const stockTitle = (m: MissingInput) => (m.stockAt ? `Read ${ago(m.stockAt)}` : "Not read yet: use Refresh stock");
    if (this.missingMode.cards) {
      return h(
        "div",
        { class: "mini-grid" },
        ...items.map((m) =>
          h(
            "article",
            { key: m.key, class: `mini-card${this.ctx.live.favorites.has(m.key) ? " favorite" : ""}`, title: stackDetail(m.stack) },
            h("div", { class: "mc-head" }, h("span", { class: "slot" }, stackIcon(m.stack, "lg")), h("span", { class: "mc-name" }, stackName(m.stack)), favoriteStar(this.ctx, m.key, m.stack)),
            h("div", { class: "mc-amount", title: stockTitle(m) }, h("strong", null, amount(m.stock, m.stack.type)), h("span", { class: "muted small" }, " in stock")),
            h(
              "details",
              { class: "small" },
              h("summary", { class: "muted" }, `Needed by ${m.usedBy} pattern${m.usedBy === 1 ? "" : "s"}`),
              h("div", { class: "chips" }, ...m.needs.map((s) => stackChip(s, null))),
            ),
            h("div", { class: "mc-actions" }, h("span", { class: "spacer" }), toggle(m)),
          ),
        ),
      );
    }
    return h(
      "div",
      { class: "table-wrap" },
      h(
        "table",
        null,
        h(
          "thead",
          null,
          h("tr", null, h("th", null, "Ingredient"), h("th", { class: "num" }, "In stock"), h("th", null, "Needed by"), h("th", null, "")),
        ),
        h(
          "tbody",
          null,
          ...items.map((m) =>
            h(
              "tr",
              { key: m.key },
              h("td", null, h("div", { class: "stack-name" }, favoriteStar(this.ctx, m.key, m.stack), stackIcon(m.stack), stackName(m.stack)), h("div", { class: "muted small" }, stackDetail(m.stack))),
              h(
                "td",
                { class: "num", title: stockTitle(m) },
                amount(m.stock, m.stack.type),
              ),
              h(
                "td",
                null,
                h("div", { class: "small muted" }, `${m.usedBy} pattern${m.usedBy === 1 ? "" : "s"}`),
                h("div", { class: "chips" }, ...m.needs.map((s) => stackChip(s, null))),
              ),
              h("td", { class: "actions" }, toggle(m)),
            ),
          ),
        ),
      ),
    );
  }
}
